import { Box, Flex, Skeleton, Text } from "@chakra-ui/react";
import { Empty, Tooltip } from "antd";
import SelectTenant from "components/Input/SelectTenant";
import { useDashboardPrincipal } from "core/features/dashboards/store";
import { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import CardItemManutencao from "./CardItemManutencao";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";
import history from "infra/helpers/history";
import colors from "resources/theme/colors";
import { useListarManutencoesInfiniteQuery } from "core/features/manutencao/hooks/useListarManutencoesInfiniteQuery";
import { StatusManutencaoEnum } from "features/manutencao/typings";
import FiltroTagStatusManutencao from "components/Tags/FiltroTagStatusManutencao";

const Loader = () => (
  <Flex flexDir="column" h="100%" justifyContent="space-between" flex={1}>
    <Skeleton mb="16px" w="100%" flex={1} />
    <Skeleton mb="16px" w="100%" flex={1} />
    <Skeleton w="100%" flex={1} />
  </Flex>
);

function CardManutencaoSubtenants() {
  const [tenantsIds, setTenantsIds] = useState<string[]>([]);
  const [statusId, setStatusId] = useState<StatusManutencaoEnum | undefined>();
  const [emAtraso, setEmAtraso] = useState<boolean>(false);

  const { query } = useDashboardPrincipal();
  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useListarManutencoesInfiniteQuery({
    ...query,
    tenantsIds,
    statusId,
    emAtraso: emAtraso ? "true" : undefined,
  });

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const lastIndex = data?.pages.length ? data?.pages.length - 1 : 0;

  const total = data?.pages[lastIndex]?.meta?.totalItems ?? 0;

  return (
    <Box
      boxShadow="0 0 24px rgba(0,0,0,.05)"
      bg="white"
      rounded="16px"
      justifyContent="center"
      alignItems="center"
      p="24px"
      mt="16px"
    >
      <Flex mb={4} gap={4} justifyContent="space-between" alignItems="center">
        <Tooltip title="Ir para a listagem de manutenções" placement="top">
          {isLoading && !isFetchingNextPage ? (
            <Skeleton w="100px" h="20px" />
          ) : (
            <Text
              fontSize="20px"
              textDecoration="underline"
              _hover={{
                color: colors.primary,
              }}
              cursor="pointer"
              onClick={() => {
                history.push("/administracao/reservas");
              }}
            >
              {total === 1
                ? `${total} manutenção`
                : `${total ?? 0} manutenções`}
            </Text>
          )}
        </Tooltip>

        <Box w="50%">
          <SelectTenant
            mode="tags"
            value={tenantsIds}
            onChange={(value) => setTenantsIds(value)}
            allowClear
            placeholder="Todos os condomínios"
            maxTagCount={1}
          />
        </Box>
      </Flex>

      <Flex mb={4} flexWrap="wrap">
        <FiltroTagStatusManutencao
          selectedTipoStatus={statusId}
          setSelectedTipoStatus={(status) => {
            setStatusId(status as StatusManutencaoEnum);
            setEmAtraso(false);
          }}
          selectedEmAtraso={emAtraso}
          onSelectEmAtraso={() => {
            setEmAtraso(!emAtraso);
            setStatusId(undefined);
          }}
        />
      </Flex>

      <Flex
        flexDir="column"
        minH="30vh"
        maxH="30vh"
        overflowY="auto"
        id="dashboard-card-manutencao-subtenants"
      >
        {!isFetching && !items.length ? (
          <Flex justifyContent="center" alignItems="center" flex={1} h="100%">
            <Empty
              description={
                !query.dataInicio || !query.dataFim
                  ? "Selecione um período para ver as manutenções"
                  : "Nenhuma manutenção encontrada"
              }
            />
          </Flex>
        ) : null}

        {isFetching && <Loader />}

        <InfiniteScroll
          scrollableTarget="dashboard-card-manutencao-subtenants"
          dataLength={items.length}
          next={() => {
            fetchNextPage();
          }}
          scrollThreshold={0.1}
          hasMore={!!hasNextPage}
          loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
        >
          <Flex flexDir="column" h="100%" flex={1} pr="4px" gap="8px">
            {items.map((manutencao) => (
              <CardItemManutencao item={manutencao} key={manutencao.id} />
            ))}
          </Flex>
        </InfiniteScroll>
      </Flex>
    </Box>
  );
}

export default CardManutencaoSubtenants;
